import type { LiveKitCameraProfile } from './liveKitCameraProfiles';

type ModelRepublishTrackSnapshot = {
  mediaStreamTrack?: {
    readyState?: MediaStreamTrackState;
    getSettings?: () => MediaTrackSettings;
  };
  sender?: {
    getParameters?: () => {
      encodings?: ArrayLike<unknown> | null;
    };
  };
};

type ModelRepublishReasonParams = {
  track: ModelRepublishTrackSnapshot | undefined;
  lastAppliedProfile: LiveKitCameraProfile | null;
  lastCaptureRecoveryDeviceId: string | null;
  currentDeviceId: string | null;
};

type ModelLowResolutionRecoveryParams = {
  captureLow: boolean;
  lowResolutionStreak: number;
  restartStreakLimit: number;
  lastCaptureRecoveryDeviceId: string | null;
  currentDeviceId: string | null;
};

export function getModelRepublishReason({
  track,
  lastAppliedProfile,
  lastCaptureRecoveryDeviceId,
  currentDeviceId,
}: ModelRepublishReasonParams) {
  const mediaStreamTrack = track?.mediaStreamTrack;
  if (!mediaStreamTrack) return 'model-camera-missing-track';
  if (mediaStreamTrack.readyState !== 'live') return 'model-camera-track-not-live';

  const settings = mediaStreamTrack.getSettings?.() ?? {};
  const width = settings.width ?? 0;
  const height = settings.height ?? 0;
  const shortEdge = Math.min(width, height);
  const encodings = Array.from(track.sender?.getParameters?.().encodings ?? []);

  if (encodings.length > 1) return 'model-camera-published-with-simulcast';
  if (
    lastAppliedProfile !== 'model' &&
    shortEdge > 0 &&
    shortEdge < 900 &&
    currentDeviceId &&
    lastCaptureRecoveryDeviceId !== currentDeviceId
  ) {
    return 'model-camera-capture-too-small';
  }

  return null;
}

export function getModelLowResolutionRecoveryAction({
  captureLow,
  lowResolutionStreak,
  restartStreakLimit,
  lastCaptureRecoveryDeviceId,
  currentDeviceId,
}: ModelLowResolutionRecoveryParams): 'request-republish' | 'sender-preferences' {
  const canRetryCaptureDevice = captureLow &&
    Boolean(currentDeviceId) &&
    lastCaptureRecoveryDeviceId !== currentDeviceId;

  if (canRetryCaptureDevice || (!captureLow && lowResolutionStreak >= restartStreakLimit)) {
    return 'request-republish';
  }

  return 'sender-preferences';
}
